/**
 * IMPORTANT: English comments only. DO NOT use Chinese characters in comments.
 * All Chinese comments will be removed during code reviews.
 */
import { toast } from 'react-hot-toast';
import addressService from '../../services/addressService';
import apiClient from '../../services/api';
import { normalizePhoneNumber } from '../../utils/phoneUtils';
import { isLocationWithinServiceArea } from './utils';

/**
 * Clean zip code to ensure it doesn't exceed length limits and contains only valid characters
 * @param zipCode Raw zip code string
 * @returns Cleaned zip code
 */
const cleanZipCode = (zipCode: string): string => {
  if (!zipCode) return '';
  
  // Remove Chinese or Thai characters (any non-Latin script)
  // This regex will match all non-ASCII characters that aren't basic Latin alphabet
  let cleaned = zipCode.replace(/[^\x00-\x7F]+/g, '');
  
  // Remove any characters that aren't alphanumeric, spaces or hyphens
  cleaned = cleaned.replace(/[^a-zA-Z0-9\s-]/g, '');
  
  // Trim and limit length to 20 characters (database constraint)
  cleaned = cleaned.trim().substring(0, 20);
  
  return cleaned;
};

/**
 * Extract city name from address details using intelligent parsing
 * @param addressDetails Full address string from Google
 * @param coordinates Location coordinates for fallback detection
 * @returns Extracted city name
 */
const extractCityFromAddress = (addressDetails: string, coordinates: { lat: number; lng: number }): string => {
  if (!addressDetails) {
    // Use coordinates to determine if we're in Chiang Mai area
    return isInChiangMaiArea(coordinates) ? 'Chiang Mai' : 'Chiang Mai'; // Default to Chiang Mai for this service
  }
  
  // Split address by comma and clean up parts
  const addressParts = addressDetails.split(',').map(part => part.trim());
  
  // Look for known Chiang Mai district/area names
  const chiangMaiKeywords = [
    'Chiang Mai', 'เชียงใหม่', 'Muang Chiang Mai', 'Mueang Chiang Mai',
    'Mae Rim', 'แม่ริม', 'San Sai', 'สันทราย', 'Hang Dong', 'หางดง',
    'San Kamphaeng', 'สันกำแพง', 'Saraphi', 'สารภี', 'Doi Saket', 'ดอยสะเก็ด'
  ];
  
  // Check each part for Chiang Mai indicators
  for (const part of addressParts) {
    // Check if this part contains any Chiang Mai keywords
    for (const keyword of chiangMaiKeywords) {
      if (part.toLowerCase().includes(keyword.toLowerCase())) {
        return 'Chiang Mai';
      }
    }
  }
  
  // If we have at least 2 parts, use the second part as city
  if (addressParts.length >= 2) {
    const potentialCity = addressParts[1];
    // Filter out common non-city terms
    const nonCityTerms = ['Province', 'District', 'Subdistrict', 'Road', 'Street', 'Soi'];
    const isNonCity = nonCityTerms.some(term => potentialCity.toLowerCase().includes(term.toLowerCase()));
    
    if (!isNonCity && potentialCity.length > 0) {
      return potentialCity;
    }
  }
  
  // Fallback: Check coordinates to determine if we're in service area
  return isInChiangMaiArea(coordinates) ? 'Chiang Mai' : 'Chiang Mai';
};

/**
 * Check if coordinates are within Chiang Mai metropolitan area
 * @param coordinates Location coordinates
 * @returns True if within Chiang Mai area
 */
const isInChiangMaiArea = (coordinates: { lat: number; lng: number }): boolean => {
  // Chiang Mai metropolitan area bounds
  const chiangMaiBounds = {
    north: 19.0,
    south: 18.6,
    east: 99.1,
    west: 98.8
  };
  
  return coordinates.lat >= chiangMaiBounds.south &&
         coordinates.lat <= chiangMaiBounds.north &&
         coordinates.lng >= chiangMaiBounds.west &&
         coordinates.lng <= chiangMaiBounds.east;
};

/**
 * Initialize address form with existing address data
 */
export const initializeFormFromAddress = (addressToEdit: any) => {
  // Default values for form fields
  const formData = {
    addressName: '',
    addressDetails: '',
    contactName: '',
    contactPhone: '',
    deliveryNote: '',
    addressLabel: 'HOME',
    serviceLanguage: 'EN',
    callPreference: 'NO_PREFERENCE',
    images: [] as string[]
  };
  
  if (!addressToEdit) return formData;
  
  // Set address name, prioritize name then locationName
  formData.addressName = addressToEdit.name || addressToEdit.locationName || '';
  
  // Set address details, if details field doesn't exist, try to build a complete address from other fields
  let detailsValue = addressToEdit.details || '';
  if (!detailsValue && addressToEdit.street) {
    detailsValue = `${addressToEdit.street || ''}`;
    if (addressToEdit.city) detailsValue += `, ${addressToEdit.city}`;
    if (addressToEdit.state) detailsValue += `, ${addressToEdit.state}`;
    if (addressToEdit.zipCode) detailsValue += ` ${addressToEdit.zipCode}`;
    if (addressToEdit.country) detailsValue += `, ${addressToEdit.country}`;
  }
  
  formData.addressDetails = detailsValue;
  formData.contactName = addressToEdit.contactName || '';
  
  // Preserve original phone number format
  formData.contactPhone = addressToEdit.contactPhone || '';
  
  formData.deliveryNote = addressToEdit.deliveryNote || '';
  formData.addressLabel = addressToEdit.addressLabel || 'HOME';
  formData.serviceLanguage = addressToEdit.serviceLanguage || 'EN';
  formData.callPreference = addressToEdit.callPreference || 'NO_PREFERENCE';
  formData.images = addressToEdit.images || [];
  
  return formData;
};

/**
 * Get user ID from token or localStorage
 */
export const getUserId = async (): Promise<string | null> => {
  // First check token in localStorage
  const token = localStorage.getItem('authToken');
  
  if (!token) {
    return null;
  }
  
  try {
    // Set the token in the headers for the API client
    apiClient.defaults.headers.common['Authorization'] = `Bearer ${token}`;
    
    // Try to get user info from the API directly
    const response = await apiClient.get('/auth/me');
    
    if (response.data && response.data.user && response.data.user.id) {
      return response.data.user.id;
    }
    
    // Fallback to checking localStorage
    const userDataRaw = localStorage.getItem('user');
    if (userDataRaw) {
      try {
        const userData = JSON.parse(userDataRaw);
        return userData.id || userData.user?.id;
      } catch (error) {
        console.error('Error parsing user data:', error);
      }
    }
    
    return null;
  } catch (error) {
    console.error('Error getting user ID:', error);
    return null;
  }
};

/**
 * Save or update address
 */
export const saveAddress = async (
  formData: {
    addressName: string;
    addressDetails: string;
    contactName: string;
    contactPhone: string;
    deliveryNote: string;
    addressLabel?: string;
    serviceLanguage?: string;
    callPreference?: string;
    images: string[];
  },
  coordinates: { lat: number; lng: number },
  isEditMode: boolean,
  addressId?: string
) => {
  try {
    // Validate required fields
    if (
      !formData.addressName.trim() || 
      !formData.addressDetails.trim() || 
      !formData.contactName.trim() || 
      !formData.contactPhone.trim()
    ) {
      toast.error('Please fill in all required fields');
      return null;
    }
    
    // Extra validation: Check if location is within service area
    const isWithinServiceArea = isLocationWithinServiceArea(coordinates);
    if (!isWithinServiceArea) {
      toast.error('This location is outside our delivery zone. Please select a location within the green outlined area on the map.');
      return null;
    }
    
    // Show save loading state
    toast.loading(isEditMode ? 'Updating address...' : 'Saving address...');
    
    // Get user ID
    const userId = await getUserId();
    if (!userId) {
      toast.dismiss();
      toast.error('You need to be logged in to save an address');
      return null;
    }
    
    // Clean up images array - filter out any blob URLs that might have snuck in
    const validImages = formData.images.filter(img => {
      // Only keep images that are valid URLs starting with https://
      if (img.startsWith('blob:')) {
        console.log('[saveAddress] Filtering out invalid blob URL:', img);
        return false;
      }
      return true;
    });
    
    if (validImages.length !== formData.images.length) {
      console.log('[saveAddress] Filtered out', formData.images.length - validImages.length, 'invalid image URLs');
    }
    
    // Normalize phone number
    // Preserve original phone number format
    
    // Create address object
    const addressData = {
      name: formData.addressName.substring(0, 100), // Limit name length
      details: formData.addressDetails.substring(0, 255), // Limit details length
      contactName: formData.contactName.substring(0, 100), // Limit contact name length
      contactPhone: formData.contactPhone, // Preserve original phone number format
      deliveryNote: formData.deliveryNote, // Remove length limitation for delivery notes
      addressLabel: formData.addressLabel || 'HOME', // Default to HOME if not provided
      serviceLanguage: formData.serviceLanguage || 'EN', // Default to EN if not provided
      callPreference: formData.callPreference || 'NO_PREFERENCE', // Default to NO_PREFERENCE if not provided
      latitude: coordinates.lat,
      longitude: coordinates.lng,
      images: validImages, // Use the validated images array
      locationName: formData.addressName.substring(0, 100), // Limit location name length
      // Add required Address properties
      street: (formData.addressDetails.split(',')[0] || formData.addressDetails).substring(0, 100), // Limit street length
      city: extractCityFromAddress(formData.addressDetails, coordinates).substring(0, 50), // Intelligent city extraction
      state: (formData.addressDetails.split(',').length > 2 ? formData.addressDetails.split(',')[2].trim() : '').substring(0, 50), // Limit state length
      // Clean zipCode to remove any non-alphanumeric chars and limit length
      zipCode: cleanZipCode(formData.addressDetails.split(',').length > 3 ? formData.addressDetails.split(',')[3].trim() : ''),
      country: 'Thailand',
      id: ''  // Add empty id field required by the Address type
    };
    
    let savedAddress;
    
    // Depending on whether it's edit mode or not, create or update address
    if (isEditMode && addressId) {
      savedAddress = await addressService.updateAddress(addressId, addressData);
    } else {
      savedAddress = await addressService.createAddress(addressData);
    }
    
    // Dismiss loading toast
    toast.dismiss();
    
    if (savedAddress) {
      toast.success(isEditMode ? 'Address updated successfully' : 'Address saved successfully');
      return savedAddress;
    } else {
      toast.error(isEditMode ? 'Failed to update address' : 'Failed to save address');
      return null;
    }
  } catch (error) {
    toast.dismiss();
    console.error('Error saving/updating address:', error);
    toast.error(isEditMode ? 'Error updating address' : 'Error saving address');
    return null;
  }
}; 